import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { orgApi } from '../services/api';
import { useAuth } from '../store/auth-context';
import type { OrgPluginCatalogSource } from '../types/plugins';

function useOrgId(): string {
  const { user } = useAuth();
  return user?.orgId ?? '';
}

function useOrgPluginCatalogApi() {
  const orgId = useOrgId();
  return useMemo(() => orgApi(orgId).pluginCatalog, [orgId]);
}

/**
 * Browsable catalog cached by the backend sync job. Any org member can read.
 *
 * `pollIntervalMs` enables short-lived polling after an admin triggers a sync —
 * callers flip it on, watch for `lastSyncedAt` to advance, then flip it back to
 * `undefined`. There's no WS event for catalog sync, so polling is the only
 * fresh-data signal.
 */
export function useOrgPluginCatalog(opts: { pollIntervalMs?: number } = {}) {
  const orgId = useOrgId();
  const api = useOrgPluginCatalogApi();
  return useQuery({
    queryKey: ['org', orgId, 'plugin-catalog'],
    queryFn: () => api.list(),
    enabled: !!orgId,
    refetchInterval: opts.pollIntervalMs ?? false,
  });
}

export function useOrgPluginCatalogSources() {
  const orgId = useOrgId();
  const api = useOrgPluginCatalogApi();
  return useQuery({
    queryKey: ['org', orgId, 'plugin-catalog', 'sources'],
    queryFn: () => api.getSources(),
    enabled: !!orgId,
  });
}

export function usePutOrgPluginCatalogSources() {
  const orgId = useOrgId();
  const api = useOrgPluginCatalogApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sources: OrgPluginCatalogSource[]) => api.putSources(sources),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['org', orgId, 'plugin-catalog', 'sources'],
      });
      // Source changes can dirty the cached catalog too.
      queryClient.invalidateQueries({
        queryKey: ['org', orgId, 'plugin-catalog'],
      });
    },
  });
}

export function useSyncOrgPluginCatalog() {
  const orgId = useOrgId();
  const api = useOrgPluginCatalogApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.sync(),
    onSuccess: () => {
      // Kick off the first refetch immediately; callers manage the polling
      // window via `useOrgPluginCatalog({ pollIntervalMs })`.
      queryClient.invalidateQueries({
        queryKey: ['org', orgId, 'plugin-catalog'],
      });
    },
  });
}
